import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { RouterService } from "../../services/router.service";
import { Router, NavigationEnd } from "@angular/router";
import { AuthenticationService } from "../../services/authentication.service";
import { PermissionEnum } from "../../model/permission-enum";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { HapticService } from "../../services/haptic.service";
import { LoginPromptService } from "../../services/login-prompt.service";

@Component({
  selector: "app-footer",
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: "./footer.component.html",
  styleUrl: "./footer.component.css",
})
export class FooterComponent implements OnInit, OnDestroy {
  hasAdminPermission = false;
  currentRoute = "";
  private routerSubscription?: Subscription;

  constructor(
    private routerService: RouterService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private hapticService: HapticService,
    private loginPromptService: LoginPromptService,
  ) {}

  ngOnInit(): void {
    this.authenticationService.currentUser.subscribe(() => {
      const permissions = this.authenticationService.currentUserPermissions;
      this.hasAdminPermission = permissions.some(
        (permission: PermissionEnum) => permission == PermissionEnum.Admin,
      );
    });

    this.currentRoute = this.router.url;
    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        this.currentRoute = event.url;
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  goToUnimplemented() {
    this.routerService.navigateTo("unimplemented");
  }

  async goToHome() {
    this.hapticService.light();
    try {
      const currentUser = this.authenticationService.currentUserValue;
      if (!currentUser) {
        this.router.navigate(["/clubs"]);
        return;
      }

      const permissions = this.authenticationService.currentUserPermissions;
      const hasAdminPermission = permissions.some(
        (permission: PermissionEnum) => permission == PermissionEnum.Admin,
      );

      if (hasAdminPermission) {
        await this.routerService.navigateTo("employee-interface");
      } else {
        await this.routerService.navigateTo("home");
      }
    } catch (error) {
      console.error("Navigation error:", error);
      this.router.navigate(["/home"]);
    }
  }

  async goToTournaments() {
    this.hapticService.light();
    try {
      const currentUser = this.authenticationService.currentUserValue;
      if (!currentUser) {
        this.loginPromptService.show(
          "Log in to view tournaments",
          "/tournaments",
        );
        return;
      }
      await this.routerService.navigateTo("tournaments");
    } catch (error) {
      console.error("Navigation error:", error);
      this.router.navigate(["/tournaments"]);
    }
  }

  goToNotification() {
    this.hapticService.light();
    try {
      const currentUser = this.authenticationService.currentUserValue;
      if (!currentUser) {
        this.loginPromptService.show(
          "Log in to view your notifications",
          "/notification",
        );
        return;
      }
      this.routerService.navigateTo("notification");
    } catch (error) {
      console.error("Navigation error:", error);
      this.loginPromptService.show(
        "Log in to view your notifications",
        "/notification",
      );
    }
  }

  goToProfile() {
    this.hapticService.light();
    try {
      const user = this.authenticationService.currentUserValue;
      if (!user) {
        this.loginPromptService.show("Log in to view your profile", "/profile");
        return;
      }
      this.routerService.navigateTo(`profile/${user.id}`);
    } catch (error) {
      console.error("Navigation error:", error);
      this.loginPromptService.show("Log in to view your profile", "/profile");
    }
  }

  isActive(route: string): boolean {
    if (route === "home") {
      const permissions = this.authenticationService.currentUserPermissions;
      const hasAdminPermission = permissions.some(
        (permission: PermissionEnum) => permission == PermissionEnum.Admin,
      );

      if (hasAdminPermission) {
        return this.currentRoute === "/employee-interface";
      }
      return (
        this.currentRoute === "/" ||
        this.currentRoute === "/home" ||
        this.currentRoute === "/clubs"
      );
    }
    if (route === "tournaments") {
      return this.currentRoute.startsWith("/tournaments");
    }
    if (route === "notification") {
      return this.currentRoute.includes("/notification");
    }
    if (route === "profile") {
      return this.currentRoute.includes("/profile");
    }
    return false;
  }
}
